import React, { useState, useCallback, useRef, useEffect } from "react";
import {
  Input,
  Button,
  MenuList,
  MenuItem,
  makeStyles,
  tokens,
  Spinner,
} from "@fluentui/react-components";
import { Add20Filled } from "@fluentui/react-icons";
import { useTranslation } from "react-i18next";
import { useMsal } from "@azure/msal-react";
import {
  searchContactFullinfos,
  getUserPhoto,
  getAccessToken,
  getAuthenticatedClient,
} from "../../Services/GraphServices";
import Person from "./Persona";
import "../CSS/AgentForm.css";
import "./CSS/SearchResourceInGraph.css";

const useMenuStyles = makeStyles({
  menuContainer: {
    position: "fixed",
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: tokens.shadow16,
    borderRadius: tokens.borderRadiusMedium,
    marginTop: "4px",
    zIndex: 10000,
    maxHeight: "250px",
    overflowY: "auto",
  },
  menuItem: {
    padding: "0 !important",
    width: "100% !important",
    minWidth: "100% !important",
  },
});

export default function SearchResourceInGraph({
  displayName = "",
  onSelectUser,
  isUpdate = false,
  disabled = false,
}) {
  const { instance, accounts } = useMsal();
  const activeAccount = accounts[0];
  const { t } = useTranslation();
  const styles = useMenuStyles();

  const [input, setInput] = useState(displayName);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showMenu, setShowMenu] = useState(false);

  const containerRef = useRef(null);
  const latestQueryRef = useRef("");
  // ✅ Handle click outside to close menu
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setShowMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // ✅ Cleanup Blob URLs on unmount
  useEffect(() => {
    return () => {
      results.forEach((r) => r.photo && URL.revokeObjectURL(r.photo));
    };
  }, [results]);

  const handleQuery = useCallback(
    async (search) => {
      if (!search || search.length < 3) {
        setResults([]);
        setShowMenu(false);
        return;
      }

      setLoading(true);
      latestQueryRef.current = search;

      try {
        const contacts = await searchContactFullinfos(instance, activeAccount, search);
        if (!contacts || contacts.length === 0) {
          setResults([]);
          setShowMenu(false);
          setLoading(false);
          return;
        }

        const token = await getAccessToken(instance, activeAccount);
        const graphClient = getAuthenticatedClient(token);

        const formatted = await Promise.all(
          contacts.map(async (c) => {
            try {
              const blob = await getUserPhoto(graphClient, c.id);
              const photoUrl = blob ? URL.createObjectURL(blob) : "";
              return {
                id: c.id,
                displayName: c.displayName,
                email: c.mail || c.userPrincipalName || "",
                userPrincipalName: c.userPrincipalName || "",
                photo: photoUrl,
              };
            } catch {
              return {
                id: c.id,
                displayName: c.displayName,
                email: c.mail || c.userPrincipalName || "",
                userPrincipalName: c.userPrincipalName || "",
                photo: "",
              };
            }
          })
        );

        if (latestQueryRef.current === search) {
          setResults(formatted);
          setShowMenu(true);
        }
      } catch (err) {
        console.error("Erreur lors de la recherche :", err);
      } finally {
        setLoading(false);
      }
    },
    [instance, activeAccount]
  );

  const handleAdd = useCallback(
  (user) => {
    setInput(user.displayName); // <-- set the input to selected user's name
    onSelectUser?.(user);
    setShowMenu(false); // optional: close menu after selection
  },
  [onSelectUser]
);
useEffect(() => {
  setInput(displayName || "");
  }, [displayName]);
  
  // Calculer la position du menu
  const getMenuPosition = () => {
    if (!containerRef.current || !showMenu) return {};
    const inputElement = containerRef.current.querySelector('.input-graph-style input');
    const rect = inputElement ? inputElement.getBoundingClientRect() : containerRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const spaceBelow = viewportHeight - rect.bottom;
    const MENU_MARGIN = 4;
    const ITEM_HEIGHT_ESTIMATE = 56; // approx height per item
    const MENU_MAX_HEIGHT = 250; // matches styles
    const estimatedMenuHeight = Math.min(results.length * ITEM_HEIGHT_ESTIMATE, MENU_MAX_HEIGHT);

    const shouldOpenUp = spaceBelow < (estimatedMenuHeight + MENU_MARGIN);
    const top = shouldOpenUp
      ? Math.max(0, rect.top - estimatedMenuHeight - MENU_MARGIN)
      : rect.bottom + MENU_MARGIN;

    return {
      top: `${top}px`,
      left: `${rect.left}px`,
      width: `${rect.width}px`,
    };
  };
  
  return (
    <div className="div-container-search" ref={containerRef}>
      <Input
        className="input-graph-style"
        value={input}
        placeholder={t("COMMENCER_ECRIRE")}
        disabled={disabled}
        onChange={(_, data) => {
          setInput(data.value);
          if (data.value.length > 2) handleQuery(data.value);
          else setShowMenu(false);
        }}
      />
      {loading && <Spinner size="tiny" className="spinner-style" />}

      {showMenu && (
        <div className={styles.menuContainer} style={getMenuPosition()}>
          <MenuList>
            {results.length > 0 ? (
              results.map((user) => (
                <MenuItem key={user.id} className={styles.menuItem}>
                  <div className="div-menu-style">
                    <Person
                      details={{ displayName: user.displayName }}
                      avatar={{ image: { src: user.photo } }}
                    />
                    <Button
                      className="btn-style-add-search"
                      size="small"
                      appearance="primary"
                      icon={<Add20Filled />}
                      onClick={() => handleAdd(user)}
                    >
                    </Button>
                  </div>
                </MenuItem>
              ))
            ) : (
              <MenuItem disabled>{t("NO_RESULTS_FOUND")}</MenuItem>
            )}
          </MenuList>
        </div>
      )}
    </div>
  );
}
